import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { metenApi } from "../services/metenApi";
import { normalizeDemoProfile } from "../utils/demoAccess";

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const { user, isSessionReady } = useAuth();
  const userId = user?.id || null;
  const [demoProfile, setDemoProfile] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState("");

  // `quiet` is for background refreshes of the usage counters: no loading
  // state, and a failed request keeps the profile already loaded instead of
  // clearing it (which would momentarily change what the account may do).
  const refreshDemoProfile = useCallback(async (options) => {
    const quiet = Boolean(options && options.quiet);
    if (!userId) {
      setDemoProfile(null);
      setDemoError("");
      setDemoLoading(false);
      return null;
    }
    if (!quiet) setDemoLoading(true);
    try {
      const payload = await metenApi.getDemoProfile();
      const normalized = normalizeDemoProfile(payload);
      setDemoProfile(normalized);
      setDemoError("");
      return normalized;
    } catch (err) {
      if (!quiet) {
        setDemoProfile(null);
        setDemoError(err?.message || "Failed to load demo profile.");
      }
      return null;
    } finally {
      if (!quiet) setDemoLoading(false);
    }
  }, [userId]);

  // Keep the usage counters current: when the user comes back to the tab and
  // every two minutes while it is visible.
  useEffect(() => {
    if (!isSessionReady || !userId) return undefined;
    const refreshIfVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      void refreshDemoProfile({ quiet: true });
    };
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    const timer = window.setInterval(refreshIfVisible, 120000);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.clearInterval(timer);
    };
  }, [isSessionReady, refreshDemoProfile, userId]);

  useEffect(() => {
    if (!isSessionReady) return;
    if (!userId) {
      setDemoProfile(null);
      setDemoError("");
      setDemoLoading(false);
      return;
    }
    void refreshDemoProfile();
  }, [isSessionReady, refreshDemoProfile, userId]);

  const value = useMemo(
    () => ({
      demoProfile,
      demoLoading,
      demoError,
      refreshDemoProfile,
    }),
    [demoError, demoLoading, demoProfile, refreshDemoProfile]
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

/** The demo context, or null outside a DemoProvider (e.g. in isolated tests). */
export function useOptionalDemo() {
  return useContext(DemoContext);
}

export function useDemo() {
  const value = useContext(DemoContext);
  if (!value) {
    throw new Error("useDemo must be used within a DemoProvider");
  }
  return value;
}
